import { useMemo, useState, useEffect } from 'react';
import { Button, Flex, Input, Select, Tooltip, Checkbox } from '@bigcommerce/big-design';
import { createSelectOptions } from '../../../../utils';
import { FILTERS } from '../hooks/useSearchFilters';
import { DateRangeFilter } from './DateRangeFilter';
import { ExportFilterAction } from './ExportFilterAction';
import { FilterFlexItem } from './FilterFlexItem';
import useVehicleFilters from '../hooks/useVehicleFilters';
import './OrdersFilters.css';

export default function OrdersFilters({
	filters,
	setFilter,
	vendors,
	tags,
	countries,
	selectedItems,
	childTags,
	preset,
}) {
	const {
		[FILTERS.VENDOR]: vendor,
		[FILTERS.TAGS]: selectedTags,
		[FILTERS.HAS_PHOTOS]: hasPhotos,
		[FILTERS.COUNTRY]: country,
		[FILTERS.CHILD_TAG]: childTag,
		[FILTERS.YEAR]: year,
		[FILTERS.MAKE]: make,
		[FILTERS.MODEL]: model,
	} = filters;
	const [keywords, setKeywords] = useState('');
	const [dateRange, setDateRange] = useState([null, null]);
	const vendorOptions = useMemo(
		() => createSelectOptions(vendors),
		[vendors],
	);
	const tagOptions = useMemo(() => createSelectOptions(tags), [tags]);
	const childTagOptions = useMemo(
		() => createSelectOptions(childTags),
		[childTags],
	);
	const countryOptions = useMemo(
		() => createSelectOptions(countries),
		[countries],
	);
	
	// Use the vehicle filters hook to get year, make, and model options from fitment table
	const { years, makes, models, loading, yearLoading, makeLoading, modelLoading } = useVehicleFilters(filters, setFilter);
	
	
	// Convert the arrays to select options
	const yearOptions = useMemo(() => createSelectOptions(years), [years]);
	const makeOptions = useMemo(() => createSelectOptions(makes), [makes]);
	const modelOptions = useMemo(() => createSelectOptions(models), [models]);

	function clearFilters() {
		setFilter(FILTERS.PRESET, 'All Orders');
		setFilter(FILTERS.VENDOR, '');
		setFilter(FILTERS.TAGS, '');
		setFilter(FILTERS.CHILD_TAG, '');
		setFilter(FILTERS.HAS_PHOTOS, '');
		setFilter(FILTERS.COUNTRY, '');
		setFilter(FILTERS.START_DATE, '');
		setFilter(FILTERS.END_DATE, '');
		setFilter(FILTERS.YEAR, '');
		setFilter(FILTERS.MAKE, '');
		setFilter(FILTERS.MODEL, '');
		setKeywords('');
		setDateRange([null, null]);
		setFilter(FILTERS.KEYWORDS, '');
	}

	return (
		<Flex
			alignContent="stretch"
			alignItems="stretch"
			flexDirection="row"
			justifyContent="flex-start"
			flexWrap="wrap"
			border="box"
			borderRadius="normal"
			padding="medium"
			style={{ gap: '1rem 0' }}
		>
			<ExportFilterAction vendor={vendor} selectedItems={selectedItems} />
			<FilterFlexItem>
				<Select
					placeholder="Filter by Vendor"
					onOptionChange={(value) => setFilter(FILTERS.VENDOR, value)}
					value={vendor}
					options={vendorOptions}
				/>
			</FilterFlexItem>
			<FilterFlexItem>
				<Select
					placeholder="Filter by Tag"
					onOptionChange={(value) => setFilter(FILTERS.TAGS, value)}
					value={selectedTags}
					options={tagOptions}
				/>
			</FilterFlexItem>
			{preset === 'CS Issue' && (
				<FilterFlexItem>
					<Select
						placeholder="Filter by Child Tag"
						onOptionChange={(value) =>
							setFilter(FILTERS.CHILD_TAG, value)
						}
						value={childTag}
						options={childTagOptions}
					/>
				</FilterFlexItem>
			)}
			<FilterFlexItem>
				<Select
					placeholder="Filter by Country"
					onOptionChange={(value) =>
						setFilter(FILTERS.COUNTRY, value)
					}
					value={country}
					filterable={false}
					options={countryOptions}
				/>
			</FilterFlexItem>
			<FilterFlexItem>
				<Select
					placeholder={loading || yearLoading ? "Loading years..." : "Filter by Year"}
					onOptionChange={(value) => {
						setFilter(FILTERS.YEAR, value);
					}}
					value={year}
					filterable={true}
					options={yearOptions}
				/>
			</FilterFlexItem>
			<FilterFlexItem>
				<Select
					placeholder={loading || makeLoading ? "Loading makes..." : "Filter by Make"}
					onOptionChange={(value) => {
						setFilter(FILTERS.MAKE, value);
					}}
					value={make}
					filterable={true}
					options={makeOptions}
					disabled={!year}
				/>
			</FilterFlexItem>
			<FilterFlexItem>
				<Select
					placeholder={loading || modelLoading ? "Loading models..." : "Filter by Model"}
					onOptionChange={(value) => {
						setFilter(FILTERS.MODEL, value);
					}}
					value={model}
					filterable={true}
					options={modelOptions}
					disabled={!make || !year}
				/>
			</FilterFlexItem>
			<DateRangeFilter
				value={dateRange}
				setDateValue={setDateRange}
				onDateRangeChange={(startDate, endDate) => {
					const startDateFilter = startDate
						? startDate.getTime()
						: '';

					setFilter(FILTERS.START_DATE, startDateFilter);
					setFilter(
						FILTERS.END_DATE,
						endDate ? endDate.getTime() : startDateFilter,
					);
				}}
			/>

			<FilterFlexItem>
				<Checkbox
					label="Has Photos"
					checked={hasPhotos}
					onChange={() => setFilter(FILTERS.HAS_PHOTOS, !hasPhotos)}
				/>
			</FilterFlexItem>

			<FilterFlexItem>
				<Tooltip
					trigger={
						<Input
							placeholder="Filter by Keyword"
							value={keywords}
							onChange={(event) => {
								setKeywords(event.currentTarget.value);
							}}
							onKeyDown={(event) =>
								event.key === 'Enter' &&
								setFilter(
									FILTERS.KEYWORDS,
									event.currentTarget.value,
								)
							}
						/>
					}
					placement="top"
				>
					Press 'Enter' to change
				</Tooltip>
			</FilterFlexItem>
			<FilterFlexItem>
				<Button variant="secondary" onClick={() => clearFilters()}>
					Clear Filters
				</Button>
			</FilterFlexItem>
		</Flex>
	);
}
